import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import T from './theme';
import { avecLibelleTraduit } from '../../i18n/libelles';
import {
  InboxOutlined, StarOutlined, ClockCircleOutlined, SendOutlined,
  FileOutlined, DeleteOutlined, BellOutlined, SafetyOutlined,
  ExperimentOutlined, CreditCardOutlined, ProfileOutlined,
  EditOutlined, SearchOutlined, SettingOutlined,
} from '@ant-design/icons';

const G = T.green;

const MAIN_FOLDERS = avecLibelleTraduit([
  { key: 'inbox',  icon: <InboxOutlined />,       badgeKey: 'inboxUnread' },
  { key: 'suivis', icon: <StarOutlined /> },
  { key: 'snooze', icon: <ClockCircleOutlined /> },
  { key: 'sent',   icon: <SendOutlined /> },
  { key: 'drafts', icon: <FileOutlined />,        badgeKey: 'drafts', grayBadge: true },
  { key: 'trash',  icon: <DeleteOutlined /> },
], 'messagerie:dossiers', { cle: 'key' });

const BNCB_FOLDERS = avecLibelleTraduit([
  { key: 'relances',   icon: <BellOutlined style={{ color: '#fbbf24' }} />,       badgeKey: 'relCrit', redBadge: true },
  { key: 'garanties',  icon: <SafetyOutlined style={{ color: '#60a5fa' }} /> },
  { key: 'expertises', icon: <ExperimentOutlined style={{ color: '#34d399' }} /> },
  { key: 'bap',        icon: <CreditCardOutlined style={{ color: '#fbbf24' }} /> },
  { key: 'bordereaux', icon: <ProfileOutlined style={{ color: '#fb7185' }} /> },
], 'messagerie:dossiers', { cle: 'key' });

function ini(n) {
  const p = String(n || '').trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || p[0]?.[1] || '')).toUpperCase();
}

function FolderRow({ f, active, badges, onFolderChange }) {
  const [hov, setHov] = useState(false);
  const badge = f.badgeKey ? badges[f.badgeKey] : null;

  return (
    <div
      onClick={() => onFolderChange(f.key)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px', borderRadius: 7, cursor: 'pointer', margin: '1px 0',
        color: '#fff',
        background: active ? 'rgba(255,255,255,.18)' : hov ? 'rgba(255,255,255,.10)' : 'transparent',
        boxShadow: 'none',
        fontWeight: active ? 600 : 400,
        transition: 'background .12s',
      }}
    >
      <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{f.icon}</span>
      <span style={{ flex: 1, fontSize: 13.5 }}>{f.label}</span>
      {badge > 0 && (
        <span style={{
          minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
          fontSize: 11, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: f.redBadge ? '#dc2626' : f.grayBadge ? 'rgba(255,255,255,.25)' : '#bbf7d0',
          color: f.redBadge ? '#fff' : f.grayBadge ? '#fff' : '#15803d',
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}

export default function SidebarDossiers({
  activeFolder, onFolderChange, onCompose, onSearch, onOpenSettings, convs, user,
}) {
  const { t } = useTranslation('messagerie');
  const navigate = useNavigate();
  const [searchHov, setSearchHov] = useState(false);
  const [composeHov, setComposeHov] = useState(false);
  const [settingsHov, setSettingsHov] = useState(false);
  const [backHov, setBackHov] = useState(false);

  const inboxUnread = convs.filter(c => c.unread).length;
  const draftsN     = convs.filter(c => c.draft).length;
  const relCrit     = convs.filter(c => c.criticality === 'critical').length;
  const badges = { inboxUnread, drafts: draftsN, relCrit };

  const agentName = user
    ? `${user.prenoms || user.prenom || ''} ${user.nom || ''}`.trim() || user.email || t('sidebar.agent')
    : t('sidebar.agent');
  const agentEmail = user?.email || '';
  const agentIni   = ini(agentName) || 'AG';

  return (
    <aside style={{
      width: 256, flexShrink: 0, background: G,
      display: 'flex', flexDirection: 'column', userSelect: 'none',
      fontFamily: T.font,
    }}>
      {/* Header : retour vers l'appli */}
      <div style={{
        padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,.12)',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <button
          onClick={() => navigate(-1)}
          onMouseEnter={() => setBackHov(true)}
          onMouseLeave={() => setBackHov(false)}
          title={t('sidebar.retour')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: backHov ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.12)',
            border: 'none', color: '#fff', borderRadius: 6,
            padding: '5px 10px', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
            transition: 'background .12s',
          }}
        >
          ← CBConnect
        </button>
        <span style={{
          flex: 1, textAlign: 'right', fontSize: 12, fontWeight: 700,
          color: 'rgba(255,255,255,.55)', letterSpacing: '.5px', textTransform: 'uppercase',
        }}>
          {t('sidebar.titre')}
        </span>
      </div>

      {/* Bouton Rédiger */}
      <div style={{ padding: '16px 16px 8px' }}>
        <button
          onClick={onCompose}
          onMouseEnter={() => setComposeHov(true)}
          onMouseLeave={() => setComposeHov(false)}
          style={{
            width: '100%', height: 40, border: 'none',
            background: composeHov ? '#f0fdf4' : '#fff',
            color: G, borderRadius: 8, fontWeight: 600, fontSize: 13.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, cursor: 'pointer', transition: 'background .12s',
            boxShadow: '0 1px 3px rgba(0,0,0,.12)',
          }}
        >
          <EditOutlined /> {t('sidebar.rediger')}
        </button>
      </div>

      {/* Barre recherche */}
      <div style={{ padding: '0 16px 12px' }}>
        <div
          onClick={onSearch}
          onMouseEnter={() => setSearchHov(true)}
          onMouseLeave={() => setSearchHov(false)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            background: searchHov ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.15)',
            borderRadius: 6, padding: '0 10px', height: 34,
            transition: 'background .12s',
          }}
        >
          <span style={{ opacity: .7, fontSize: 12 }}><SearchOutlined /></span>
          <span style={{ flex: 1, fontSize: 13, color: 'rgba(255,255,255,.7)' }}>{t('sidebar.rechercher')}</span>
        </div>
      </div>

      {/* Dossiers principaux */}
      <div className="cb-scroll" style={{ flex: 1, overflowY: 'auto', padding: '0 8px' }}>
        {MAIN_FOLDERS.map(f => (
          <FolderRow
            key={f.key} f={f}
            active={activeFolder === f.key}
            badges={badges}
            onFolderChange={onFolderChange}
          />
        ))}

        {/* Section BNCB */}
        <div style={{
          fontSize: 11, letterSpacing: '.6px', fontWeight: 600,
          color: 'rgba(255,255,255,.45)', padding: '14px 12px 6px',
        }}>
          {t('sidebar.dossiersBncb')}
        </div>
        {BNCB_FOLDERS.map(f => (
          <FolderRow
            key={f.key} f={f}
            active={activeFolder === f.key}
            badges={badges}
            onFolderChange={onFolderChange}
          />
        ))}

        <div style={{ padding: '8px 4px 12px' }}>
        </div>
      </div>

      {/* Footer utilisateur */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,.15)', padding: '12px',
        display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: '#fff', color: G,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 600, fontSize: 12, flexShrink: 0,
        }}>
          {agentIni}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: '#fff',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {agentName}
          </div>
          <div style={{
            fontSize: 11, color: 'rgba(255,255,255,.6)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {agentEmail}
          </div>
        </div>
        <button
          onClick={onOpenSettings}
          onMouseEnter={() => setSettingsHov(true)}
          onMouseLeave={() => setSettingsHov(false)}
          title={t('sidebar.parametres')}
          style={{
            background: 'transparent', border: 'none', padding: 4, borderRadius: 4,
            fontSize: 16, cursor: 'pointer',
            color: settingsHov ? '#fff' : 'rgba(255,255,255,.75)',
            transition: 'color .12s',
          }}
        >
          <SettingOutlined />
        </button>
      </div>
    </aside>
  );
}

